import React, { useState, useMemo, useEffect } from 'react';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import './FormDialog.scss';
import { Box } from '@mui/material';
import Typography from 'apollo-react/components/Typography';
import { useNavigate } from 'react-router-dom';
import { API_URL, SYS_ROLE } from '../common/Constants';
import { getApi, setURL, redirectToLogin } from '../common/Utils';
import userList from '../json/userList.json'
import Autocomplete from '@mui/material/Autocomplete';
import selectItemUserRoleList from '../json/selectItemUserRoleList.json';

// テーブル定義
const UserTable = ({ orgLevel0Code, selectedRowId, setSelectedRowId, onRowSelect, userListType = '0' }) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const [filters, setFilters] = useState({ usrName: '', comp: '', dept: '', email: '', usrroleDesc: '' });
    const [data, setData] = useState([]);
    // 権限の選択リスト
    const [userRoleList, setUserRoleList] = useState([]);

    // テーブル用カラム定義
    const columns = [
        {
            accessorKey: 'usrName',
            header: t('userName')
        },
        { 
            accessorKey: 'comp',
            header: t('org') 
        },
        { 
            accessorKey: 'dept', 
            header: t('department') 
        },
        { 
            accessorKey: 'email', 
            header: t('email') 
        },
        { 
            accessorKey: 'usrroleDesc', 
            header: t('userPermission') 
        },
    ];

    // ダイアログ種別別定数
    const DIALOG_STATUS = {
        NO_DIALOG: '0',
        OK_DIALOG: '1',
        DETAIL_DIALOG: '2',
        SAVE_CONFIRM_DIALOG: '3',
        DELETE_FOLDER_CONFIRM_DIALOG: '4',
        COMPLETE_DIALOG: '5',
        OK_DIALOG_BACK: '6',
        DETAIL_DIALOG_BACK: '7',
    };

    // 確認ダイアログ表示用フラグ
    const [dialogStatus, setDialogStatus] = useState(DIALOG_STATUS.NO_DIALOG);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogContent, setDialogContent] = useState('');
    const [detailContent, setDetailContent] = useState('');

    // バックエンドからデータを取得するためのuseEffectフック
    useEffect(() => {
        (async () => {
            if (window.localStorage.getItem(['token']) === null) {
                navigate('/')
                return;
            }
            // ユーザ権限検索ボックスは初期値として"すべて"を設定
            setFilters(prev => ({ ...prev, usrroleDesc: 'すべて' }));

            // ユーザ一覧API
            const url = setURL(API_URL.SELECT_ITEM_USERLIST) + '?';
            const params = {};
            //パラメーター：取得ユーザリスト区分（0:全ユーザ、1:IQVIA社員のみ）
			params['userListType'] = userListType;
            const urlSearchParam = String(new URLSearchParams(params));
            const getApiParam = {
                url: url + urlSearchParam,
                sysRole: SYS_ROLE.USER_SEARCH,
                success: userListApiSuccess,
                error: userListApiError,
                json: userList,
            }
            await getApi(getApiParam);

            // 権限一覧取得取得API
			const urlUsrroleList = setURL(API_URL.SELECT_ITEM_USER_ROLE_LIST) + '?';
			// クエリパラメータ作成
			const getRoleListparams = {};
			getRoleListparams['showAdmin'] = true;
			const urlRoleListParam =  String(new URLSearchParams(getRoleListparams));
			const getUsrroleListApiParam = {
				url: urlUsrroleList + urlRoleListParam,
				sysRole: SYS_ROLE.USER_MANAGEMENT,
				success: roleListApiSuccess,
				error: roleListApiError,
				json: selectItemUserRoleList
			}
			await getApi(getUsrroleListApiParam);
        })();
    }, [userListType]);


    /**
//  * 検索用ユーザーデータ一覧取得処理成功時処理
//  * @param {array} response 
//  */
    function userListApiSuccess(response) {
        // 取得したレスポンスを各リストに格納
        setData(response);
        setInitialRowCount(response.length);
    };

    function userListApiError(error) {
        const detailErrorMessage = 'ユーザーの取得処理でエラーが発生しました。\n(An error occurred during the select item process.)';
        commonError(error, detailErrorMessage);

        //認証期限切れの場合は再ログイン
        redirectToLogin();
    };

    /**
         * 権限情報取得APIの成功時の処理
         * @param {array} response 権限情報取得APIのレスポンス
         */
        function roleListApiSuccess(response) {
            // 権限一覧をセット										
            setUserRoleList(response);
        };
    
        /**
         * 権限情報取得APIの失敗時の処理											
         * @param {object} error エラーオブジェクト
         */
        function roleListApiError(error) {
            const detailErrorMessage = '権限情報取得処理でエラーが発生しました。\n(An error occurred during the getrolelist process.)';
            commonError(error, detailErrorMessage);
    
            //認証期限切れの場合は再ログイン
            redirectToLogin();
        };

    /**
     * 共通エラー処理
     * @param {object} error エラーオブジェクト
     * @param {string} detailErrorMessage 詳細ダイアログに設定するエラーメッセージ
     */
    const commonError = (error, detailErrorMessage) => {
        if (error.name === 'MaintenanceApiError') {
            navigate('/', { state: { maintenanceFlg: true, } });
        } else if (error.name === 'CustomApiError') {
            const message = error.message;
            setDialogStatus(DIALOG_STATUS.OK_DIALOG);
            setDialogOpen(true);
            setDialogContent(message);
        } else {
            setDialogStatus(DIALOG_STATUS.DETAIL_DIALOG);
            setDialogOpen(true);
            setDialogContent(detailErrorMessage);
            setDetailContent(error.stack);
        }
    };

    // 検索条件に応じて表示を調整
    const filteredData = useMemo(() => data.filter(row => {
        return (
            row.usrName.toLowerCase().includes(filters.usrName.toLowerCase()) &&
            row.comp.toLowerCase().includes(filters.comp.toLowerCase()) &&
            row.dept.toLowerCase().includes(filters.dept.toLowerCase()) &&
            row.email.toLowerCase().includes(filters.email.toLowerCase()) &&
            (filters.usrroleDesc === '' || filters.usrroleDesc === 'すべて' || row.usrroleDesc === filters.usrroleDesc)
        );
    }), [filters, data]);

    // 企業リストの選択肢を現在の表示に応じて切り替え
    const compOptions = useMemo(() => {
        const comps = filteredData.map(user => user.comp);
        return [...new Set(comps)];
    }, [filteredData]);

    // 企業選択が変更された際の部署を削除する処理　※deptを消さないと消せなくなるため
    const deptDelete = useMemo(() => {
        filters.dept = '';
    }, [filters.comp]);

    // 部署リストの選択肢を現在の表示に応じて切り替え
    const deptOptions = useMemo(() => {
        const depts = filteredData.map(user => user.dept);
        return [...new Set(depts)];
    }, [filteredData]);

    const currentData = useMemo(() => filteredData, [filteredData]); // 全データを表示

    // テーブルへの表示用
    const table = useReactTable({
        data: currentData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: row => row.usrCode,
    });

    // 行をクリックしたときの処理
    const handleRowClick = (rowId) => {
        setSelectedRowId(rowId);
        const selectedRow = data.find(row => row.usrCode === rowId);
    };

    // 行をダブルクリックしたときの処理
    const handleRowDoubleClick = (rowId) => {
        setSelectedRowId(rowId);
        const selectedRow = data.find(row => row.usrCode === rowId);
        onRowSelect(selectedRow); // 選択された行のデータを渡す
    };

    // 検索条件のいずれかが変更された時の処理
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const [initialRowCount, setInitialRowCount] = useState();
    const [deptFlag, setDeptFlag] = useState(true);

    return (
        <div style={{ width: '100%' }}>
            <div className='filter'>
                <DialogContentText>
                    {t('Search Filter')}
                </DialogContentText>
                <div
                    className='searchResult'
                >
                    {table.getRowModel().rows.length}/{initialRowCount}
                </div>
                <Box display="flex" className="searchLabel" alignItems="center" width="100%">
                    <Typography
                        className="searchLabelText"
                    >
                        {t('userName')}
                    </Typography>
                    <Typography
                        className="searchLabelText"
                    >
                        {t('org')}
                    </Typography>
                    <Typography
                        className="searchLabelText"
                    >
                        {t('department')}
                    </Typography>
                    <Typography
                        className="searchLabelText"
                    >
                        {t('email')}
                    </Typography>
                    <Typography
                        className="searchLabelText"
                    >
                        {t('userPermission')}
                    </Typography>
                </Box>
                <Box display="flex" className="searchText" alignItems="center" width="100%">
                    <TextField
                        className="searchTextfield"
                        type="text"
                        id="usrName"
                        name="usrName"
                        value={filters.usrName}
                        onChange={handleFilterChange}
                    />
                    <Autocomplete
                        options={compOptions}
                        type="text"
                        id="org"
                        name="org"
                        className="searchTextfield"
                        renderInput={(params) => <TextField {...params} />}
                        onChange={handleFilterChange}
                        inputValue={filters.comp}
                        onInputChange={(event, newValue) => {
                            setFilters((prevFilters) => ({
                                ...prevFilters,
                                comp: newValue,
                            }));
                            if (newValue === '' || newValue === undefined) {
                                setDeptFlag(true);
                            } else {
                                setDeptFlag(false);
                            }
                        }}
                        freeSolo
                    />
                    <Autocomplete
                        options={deptOptions}
                        type="text"
                        id="dept"
                        name="dept"
                        className="searchTextfield"
                        renderInput={(params) => <TextField {...params} />}
                        onChange={handleFilterChange}
                        inputValue={filters.dept}
                        onInputChange={(event, newValue) => {
                            setFilters((prevFilters) => ({
                                ...prevFilters,
                                dept: newValue,
                            }));
                        }}
                        disabled={deptFlag}
                        freeSolo
                    />
                    <TextField
                        className="searchTextfield"
                        value={filters.email}
                        onChange={handleFilterChange}
                        type="text"
                        id="email"
                        name="email"
                    />
                    <Autocomplete
                        options={["すべて", ...userRoleList.map(role => role.usrroleDesc)]}
                        type="text"
                        id="usrroleDesc"
                        name="usrroleDesc"
                        className="searchTextfield"
                        onChange={(event, newValue) => {
                            setFilters((prevFilters) => ({
                                ...prevFilters,
                                usrroleDesc: newValue || '',
                            }));
                        }}
                        value={filters.usrroleDesc}
                        freeSolo={false}
                        disableClearable={true}
                        isOptionEqualToValue={(option, value) => option === value}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                inputProps={{
                                    ...params.inputProps,
                                    readOnly: true,
                                }}
                            />
                        )}
                    />
                </Box>
            </div>
            <div className='tableStyle'>
                <table className='userSearchResultTable'>
                    <thead className='tableHeader'>
                        {/* ヘッダー行の背景色を設定 */}
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th className={`header-${header.id} tableHeaderColumn`} key={header.id}>
                                        {header.isPlaceholder ? null : header.column.columnDef.header}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length === 0 ? (
                            <tr>
                                <td colSpan={table.getHeaderGroups()[0].headers.length} style={{ textAlign: 'center' }}>
                                    {t('message.1000100')}
                                </td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map(row => (
                                <tr
                                    key={row.id}
                                    onClick={() => handleRowClick(row.id)}
                                    onDoubleClick={() => handleRowDoubleClick(row.id)}
                                    className='selectRow'
                                    style={{
                                        backgroundColor: selectedRowId === row.id ? '#add8e6' : 'white',
                                        color: selectedRowId === row.id ? '#ffffff' : 'black',
                                    }}
                                >
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id}>
                                            {cell.column.columnDef.cell(cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ダイアログ全般処理　ここからテーブル定義を呼び出す
const FormDialog = ({ orgLevel0Code, disabled, onClose, userListType = '0' }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [t] = useTranslation();
    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleRowSelect = (rowData) => {
        const response = {};
        response['usrName'] = rowData.usrName; // ユーザー名を設定
        response['usrCode'] = rowData.usrCode; // useridを設定
        setIsModalOpen(false); // モーダルを閉じる
        onClose(response);
    };

    return (
        <React.Fragment>
            <Button disabled={disabled} className='userSearchBtn' variant="outlined" onClick={handleOpenModal}>
                {t('adduser')}
            </Button>
            <Dialog
                fullWidth={true}
                maxWidth='100rem'
                className='searchBox'
                open={isModalOpen}
                PaperProps={{
                    component: 'form',
                }}
            >
                <div className='titlebar'>
                    <DialogTitle className='title'>
                        <IconButton className='close' onClick={handleCloseModal}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                fill="none"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="feather feather-x"
                            >
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </IconButton>
                        {t('userSearch')}
                    </DialogTitle>
                </div>
                <UserTable 
                    orgLevel0Code={orgLevel0Code} 
                    disabled={disabled} 
                    selectedRowId={selectedRowId} 
                    setSelectedRowId={setSelectedRowId} 
                    onRowSelect={handleRowSelect} 
                    handleCloseModal={handleCloseModal}
                    userListType={userListType} // userListType を追加
                    />
            </Dialog>
        </React.Fragment>
    );
};

export default FormDialog;